import { useEffect, useState } from 'react';
import { fetchEras, type EraEntry } from '../api/setting';
import { ca } from '../i18n/ca';

/**
 * What the eras list says until the platform answers, and when it cannot: the
 * four eras and the four WW2 theatres it had before the table, by their
 * Catalan names. The platform's own list replaces it as soon as it arrives.
 */
const THEATRE_IDS = ['north_africa', 'western_europe', 'eastern_front', 'pacific'];
export const FALLBACK_ERAS: EraEntry[] = ['modern', 'coldwar', 'vietnam', 'ww2'].map((id) => ({
  id,
  label: ca.eras[id] ?? id,
  theatres:
    id === 'ww2'
      ? THEATRE_IDS.map((t) => {
          const label = ca.missionUi.theatre.names[t] ?? t;
          return { id: t, label, chip: label, words: [] };
        })
      : [],
}));

let loaded: Promise<EraEntry[]> | null = null;

/** The platform's list, asked once per page load and shared by every component. */
function load(): Promise<EraEntry[]> {
  if (!loaded) {
    loaded = fetchEras()
      .then((eras) => (Array.isArray(eras) && eras.length > 0 ? eras : FALLBACK_ERAS))
      .catch(() => {
        loaded = null;
        return FALLBACK_ERAS;
      });
  }
  return loaded;
}

/**
 * The eras a night can be set in and each one's theatres
 * (`era/theatres.json`, `GET /api/setting/eras`), for the era chips, the
 * theatre select and the catalogue's filters.
 */
export function useEras(): EraEntry[] {
  const [eras, setEras] = useState<EraEntry[]>(FALLBACK_ERAS);
  useEffect(() => {
    let live = true;
    void load().then((e) => {
      if (live) setEras(e);
    });
    return () => {
      live = false;
    };
  }, []);
  return eras;
}

/** One era's theatres, or none. */
export const theatresOf = (eras: EraEntry[], era: string) =>
  eras.find((e) => e.id === era)?.theatres ?? [];

/** An era's name, from the platform's list, else the web's own, else its id. */
export const eraLabel = (eras: EraEntry[], era: string) =>
  eras.find((e) => e.id === era)?.label ?? ca.eras[era] ?? era;
